import { useTranslation } from 'react-i18next';
import { CompassIcon, FilterIcon, UserRoundIcon, ZapIcon, type LucideIcon } from 'lucide-react';

import { AnalysisMarkdown } from '../../../../components';
import type { CreativeBrandContent } from '../../model/creativeLandscapeData.schema';

export interface AxisComparison {
  /** The axis end being out-indexed, e.g. "Rational". */
  label: string;
  advertisers: string[];
}

interface BrandDetailProps {
  content: CreativeBrandContent;
  /** Who scores higher than this brand on each axis; empty lists are simply not rendered. */
  comparisons: AxisComparison[];
}

const NAMED_LIMIT = 2;

const boxClassName = 'flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl';

const bodyClassName = 'text-pretty text-body-md text-foreground-alt';

/** The centred pill that heads each box on this page. */
const BoxChip = ({ icon: Icon, children }: { icon: LucideIcon; children: string }) => (
  <span className="inline-flex items-center gap-2 self-center rounded-full bg-surface px-3.5 py-1.5 text-body-md dark:bg-surface-raised font-medium whitespace-nowrap text-secondary-foreground">
    <Icon className="size-4 shrink-0" />
    {children}
  </span>
);

const AxisComparisonLine = ({ label, advertisers }: AxisComparison) => {
  const { t } = useTranslation('analysis');
  const named = advertisers.slice(0, NAMED_LIMIT);
  const remaining = advertisers.length - named.length;

  return (
    <span>
      <span className="font-medium text-foreground-alt">
        {t('analysisResult.creativeLandscape.moreAxis', { axis: label })}
      </span>{' '}
      {named.join(', ')}
      {remaining > 0 && ` ${t('analysisResult.creativeLandscape.moreCount', { count: remaining })}`}
    </span>
  );
};

export const BrandDetail = ({ content, comparisons }: BrandDetailProps) => {
  const { t } = useTranslation('analysis');
  const withAdvertisers = comparisons.filter((c) => c.advertisers.length > 0);

  return (
    <div className="grid grid-cols-1 items-stretch gap-sm md:grid-cols-2">
      <div className={boxClassName}>
        <BoxChip icon={CompassIcon}>
          {t('analysisResult.creativeLandscape.creativeBrandPositioning')}
        </BoxChip>
        <AnalysisMarkdown text={content.positioningSentence} className={bodyClassName} />
        {withAdvertisers.length > 0 && (
          <div className="flex flex-col gap-1 text-body-md text-muted-foreground">
            {withAdvertisers.map((comparison) => (
              <AxisComparisonLine key={comparison.label} {...comparison} />
            ))}
          </div>
        )}
      </div>

      <div className={boxClassName}>
        <BoxChip icon={UserRoundIcon}>
          {t('analysisResult.creativeLandscape.creativePersona')}
        </BoxChip>
        <AnalysisMarkdown text={content.persona} className={bodyClassName} />
      </div>

      <div className={boxClassName}>
        <BoxChip icon={ZapIcon}>{t('analysisResult.creativeLandscape.creativeTactics')}</BoxChip>
        <AnalysisMarkdown text={content.tactics} className={bodyClassName} />
      </div>

      <div className={boxClassName}>
        <BoxChip icon={FilterIcon}>{t('analysisResult.creativeLandscape.funnelStrategy')}</BoxChip>
        {[
          { label: t('analysisResult.creativeLandscape.upperFunnel'), body: content.funnel.upper },
          { label: t('analysisResult.creativeLandscape.lowerFunnel'), body: content.funnel.lower },
        ].map((stage) => (
          <div key={stage.label} className="flex flex-col gap-1">
            <span className="text-body-md font-medium text-foreground">{stage.label}</span>
            <AnalysisMarkdown text={stage.body} className={bodyClassName} />
          </div>
        ))}
      </div>
    </div>
  );
};
